/*
 * Product-page review spotlight: shows one real eBay review, re-rolled on
 * every page load.
 *
 * The pool is already narrowed server-side by sections/product-review-spotlight.liquid
 * (style+model, then model, then shop-wide) and `data-scope` says which of the
 * three we ended up with. The heading follows the scope so a shop-wide review
 * is never dressed up as being about the product on screen.
 *
 * The pick has to happen here rather than in Liquid: Liquid has no random
 * filter, and product pages are served from Shopify's cache, so a server-side
 * choice would show the same review to everyone until the cache turned over.
 */
class ProductReviewSpotlight extends HTMLElement {
  connectedCallback() {
    const pool = this.parsePool();
    if (!pool.length) {
      this.remove();
      return;
    }

    this.render(pool[Math.floor(Math.random() * pool.length)]);
  }

  parsePool() {
    const node = this.querySelector('[data-review-pool]');
    if (!node) return [];

    try {
      const parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed) ? parsed.filter((entry) => entry && entry.b) : [];
    } catch (error) {
      console.error('Review spotlight: could not parse the review pool.', error);
      return [];
    }
  }

  get heading() {
    const map = {
      product: this.dataset.headingProduct,
      model: this.dataset.headingModel,
      shop: this.dataset.headingShop,
    };
    return map[this.dataset.scope] || map.shop || 'What our customers say';
  }

  render(review) {
    const slot = this.querySelector('[data-review-slot]');
    if (!slot) return;

    // Which listing it was left for. Shown by default, and the only thing
    // making a fallback review honest when it is about a different style.
    const listing =
      this.dataset.showListing === 'true' && review.i
        ? `<span class="review-spotlight__listing">on ${this.escape(review.i)}</span>`
        : '';

    const meta = [this.escape(review.a), review.p ? this.escape(review.p) : '', listing]
      .filter(Boolean)
      .join('<span class="review-spotlight__dot" aria-hidden="true">·</span>');

    const link =
      this.dataset.linkUrl && this.dataset.linkLabel
        ? `<a class="review-spotlight__link" href="${this.escape(this.dataset.linkUrl)}">
             ${this.escape(this.dataset.linkLabel)}
           </a>`
        : '';

    slot.innerHTML = `
      <figure class="review-spotlight__card">
        <p class="review-spotlight__heading">${this.escape(this.heading)}</p>
        <blockquote class="review-spotlight__quote">${this.escape(review.b)}</blockquote>
        <figcaption class="review-spotlight__meta">${meta}</figcaption>
        ${link}
      </figure>`;
  }

  escape(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }
}

if (!customElements.get('product-review-spotlight')) {
  customElements.define('product-review-spotlight', ProductReviewSpotlight);
}
